import { useMutation } from '@apollo/client/react';
import type { FC, ReactNode } from 'react';

import AnalyticsSection from '@zen/Accounts/AccountForms/CreateAccountForm/AnalyticsSection';
import { useUpdateAccountAnalyticsSettings } from '@zen/Accounts/AccountForms/hooks/useUpdateAccountAnalyticsSettings';
import { Form, FormButtons, FormInput } from '@zen/Components';
import type { FormInstance } from '@zen/Components/types';
import { Button, Modal } from '@zen/DesignSystem';
import { useNotification } from '@zen/utils/hooks/useNotification';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performFormMutation } from '@zen/utils/performMutation';

import {
  CreateAccountDivisionDocument,
  type CreateAccountDivisionMutationResult,
  type CreateAccountDivisionMutationVariables
} from '../../graphql';
import businessUnitValidation from './createBusinessUnitModal.validation';
import type { CreateBusinessUnitValues } from './types';

interface Props {
  accountId: string;
  isOpen: boolean;
  onClose: () => void;
  referencePrefix?: string | null;
  showAdminSettings: boolean;
}

interface AnalyticsSettings {
  analyticsEnabled: boolean;
  analyticsSharedUserEnabled: boolean;
}

const CreateBusinessUnitModal: FC<Props> = (props) => {
  const { isOpen, onClose, accountId, referencePrefix, showAdminSettings } = props;
  const [createAccountDivision] = useMutation<CreateAccountDivisionMutationResult, CreateAccountDivisionMutationVariables>(
    CreateAccountDivisionDocument
  );
  const { addSuccess, addError } = useNotification();
  const { handleUpdateAccountAnalyticsSettings, loading } = useUpdateAccountAnalyticsSettings();

  const initialValues: CreateBusinessUnitValues & { analyticsSettings: AnalyticsSettings } = {
    analyticsSettings: {
      analyticsEnabled: true,
      analyticsSharedUserEnabled: true
    },
    divisionName: '',
    referencePrefix,
    accountId
  };

  const handleCreateAccountDivision = async (values: CreateBusinessUnitValues): Promise<IOkOrErrorResult> => {
    const { analyticsSettings, ...rest } = values;

    return performFormMutation({
      mutationFn: () =>
        createAccountDivision({
          awaitRefetchQueries: true,
          refetchQueries: ['getAccountDetails', 'getAccountBusinessUnits'],
          variables: {
            input: rest
          }
        }),
      onSuccess: async (data) => {
        addSuccess('Business unit created.');

        const businessUnitId: string = data?.createAccountDivision?.accountId || '';

        if (businessUnitId && showAdminSettings) {
          await handleUpdateAccountAnalyticsSettings(businessUnitId, analyticsSettings);
        }
      },
      onError: () => addError()
    });
  };

  const renderFormButtons = ({ isSubmitting }: FormInstance<{ members: string[] }>): ReactNode => (
    <FormButtons isSubmitting={isSubmitting} layout="fixed" text="Create division">
      <Button data-testid="cancel-button" onClick={onClose} variant="ghost">
        Cancel
      </Button>
    </FormButtons>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      tagline="Manage separate bookings, products and notifications."
      title="Create division"
    >
      <Form
        formButtons={renderFormButtons}
        initialValues={initialValues}
        onSubmit={handleCreateAccountDivision}
        onSuccess={onClose}
        validationSchema={businessUnitValidation}
      >
        {() => {
          return (
            <div className="flex flex-col gap-6">
              <FormInput helperText='For example: "EMEA".' label="Division name" name="divisionName" noMargin={true} />
              {showAdminSettings && <AnalyticsSection isDisabled={loading} variant="compact" />}
            </div>
          );
        }}
      </Form>
    </Modal>
  );
};

export default CreateBusinessUnitModal;
